import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "schema/data/resource";
import { CheckIcon } from "assets/icons";
import { GeneralSelectInput } from "components/molecules";
import { useUserData } from "hooks";
import "./AppGSLinkSection.scss";

const client = generateClient<Schema>();

interface GSOption {
  label: string;
  value: string;
}

const AppGSLinkSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { userData } = useUserData();
  const [gsOptions, setGsOptions] = useState<GSOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGenuineStudents = async () => {
      if (!userData?.id) return;

      try {
        setLoading(true);
        const result = await client.models.GenuineStudent.list({
          filter: {
            userId: { eq: userData.id },
          },
        });

        if (result.data && result.data.length > 0) {
          const options: GSOption[] = result.data.map((gs) => ({
            label: `${gs.genuineStudentId} (${
              gs.updatedAt
                ? new Date(gs.updatedAt).toLocaleDateString()
                : "Draft"
            })`,
            value: gs.genuineStudentId,
          }));

          setGsOptions(options);
        } else {
          setGsOptions([]);
        }
      } catch (err) {
        console.error("Failed to fetch Genuine Student assessments:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGenuineStudents();
  }, [userData?.id]);

  if (loading) {
    return (
      <div className="gs-link-section">
        <div className="loading">Loading Genuine Student assessments...</div>
      </div>
    );
  }

  return (
    <div className="gs-link-section">
      <div className="section-header">
        <h3>Link Genuine Student Assessment</h3>
        <p>
          Select which Genuine Student Assessment you want to link to this
          application.
        </p>
      </div>

      <div className="form-fields">
        <GeneralSelectInput
          id="genuineStudentId"
          label="Select Genuine Student Assessment"
          options={[
            { label: "None - Skip this step", value: "" },
            ...gsOptions,
          ]}
          value={values.genuineStudentId || ""}
          onChange={(e) => setFieldValue("genuineStudentId", e.target.value)}
          onBlur={() => {}}
          disabled={gsOptions.length === 0}
        />

        {gsOptions.length === 0 && (
          <p className="info-text">
            No Genuine Student assessments found. You can skip this step or
            create one first.
          </p>
        )}

        {values.genuineStudentId && (
          <div className="success-indicator">
            <CheckIcon />
            <span>
              This application will be linked to your selected Genuine Student
              Assessment.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppGSLinkSection;
